import * as yup from "yup";
import {
  FilterDefinition,
  GetResultContext,
} from "components/Shared/FilterEditor/utils";
import {
  createMakeGetManyFilterResult,
  getBooleanFilterDefinition,
  getDateTimeFilterDefinition,
  getIntegerFilterDefinition,
  getSearchFilterDefinition,
  getSelectFilterDefinition,
  getStringSelectFilterDefinition,
} from "components/Shared/FilterEditor/standardFilters";
import {
  RetailerDistributionCenterFilter,
  RetailerFilter,
  RetailerOrderingPlatformFilter,
  RetailerCategoryReviewOption,
  RetailerPricingStrategyOption,
  RetailerSlottingRequiredOption,
  StringFilter,
  RetailerType,
} from "__generated__/gql/graphql";
import OrderingPlatformSelect from "components/Shared/Select/OrderingPlatformSelect";
import DistributionCenterSelect from "components/Shared/Select/DistributionCenterSelect";
import EnumSelect from "components/Shared/Select/EnumSelect";
import RetailerSelect from "components/Shared/Select/RetailerSelect";
import {
  pricingStrategyMap,
  retailerCategoryReviewMap,
  slottingOptionsMap,
} from "components/Shared/RetailerDirectory/utils";
import { getEnumSchema } from "utils/schemas/utils";
import { createNestedAddressFilterDefinitions } from "components/Shared/FilterEditor/addressFilterDefinitions";
import RetailerChannelSelect from "components/Shared/Select/RetailerChannelSelect";
import RetailerRegionSelect from "components/Shared/Select/RetailerRegionSelect";
import { INCLUDE_CUSTOM_FILTER_NAME } from "components/Shared/IncludeCustomFilter";
import { makeEnsureType } from "utils/typescriptUtilis";
import {
  RETAILER_TYPE_LABELS,
  getIsEveryOptionSelected,
} from "components/Shared/RetailerDirectory/RetailerTypeCheckboxes";
import {
  assertIsBoolean,
  getAssertedArrayOfType,
  makeAssertIsEnum,
} from "utils/assertions";
import { IS_ARCHIVED_FILTER_NAME } from "components/Shared/ArchivedStatusButtonGroup";

const filterObj = makeEnsureType<RetailerFilter>();

const makeGetManyResult = createMakeGetManyFilterResult<RetailerFilter>();
const makeGetManyOrderingPlatformsResult =
  createMakeGetManyFilterResult<RetailerOrderingPlatformFilter>();

function makeGetDcResult(
  getInnerFilter: (
    innerFilter: StringFilter,
  ) => RetailerDistributionCenterFilter,
) {
  const getOpResult = makeGetManyOrderingPlatformsResult(
    "retailerDistributionCenters",
    getInnerFilter,
  );
  return function getDcResult(value: unknown, context: GetResultContext) {
    return makeGetManyResult("retailerOrderingPlatforms", () =>
      getOpResult(value, context),
    )(value, context);
  };
}

export default function getFilterDefinitions(isAdmin?: boolean) {
  const addressFilterDefs = createNestedAddressFilterDefinitions(
    "headquartersAddress",
    { groupLabel: "Headquarters" },
  );

  const definitions: FilterDefinition[] = [
    getSearchFilterDefinition({
      getResult: (search) => ({
        nameWithUniqueSuffix: { includesInsensitive: search },
      }),
    }),
    getStringSelectFilterDefinition({
      id: "retailer",
      label: "Retailer Name",
      getResult: (value, { filterVariantDefinition: { id: variantId } }) => ({
        nameWithUniqueSuffix: { [variantId]: value },
      }),
      getSelectResult: (
        value,
        { filterVariantDefinition: { id: variantId } },
      ) => ({ id: { [variantId]: value } }),
      schema: yup.string().min(2),
      selectSchema: yup.string().uuid(),
      renderSelectInput: ({ field }, { isMulti }) => (
        <RetailerSelect
          {...field}
          isAdmin={isAdmin}
          isMulti={isMulti}
          menuPortalTarget={document.body}
        />
      ),
    }),
    getStringSelectFilterDefinition({
      id: "parent",
      label: "Parent Name",
      getResult: (value, { filterVariantDefinition: { id: variantId } }) =>
        filterObj({
          parent: { nameWithUniqueSuffix: { [variantId]: value } },
        }),
      getSelectResult: (
        value,
        { filterVariantDefinition: { id: variantId } },
      ) => filterObj({ parentId: { [variantId]: value } }),
      schema: yup.string().min(2),
      selectSchema: yup.string().uuid(),
      renderSelectInput: ({ field }, { isMulti }) => (
        <RetailerSelect
          {...field}
          isAdmin={isAdmin}
          isMulti={isMulti}
          menuPortalTarget={document.body}
          filter={{ isParent: { equalTo: true } }}
        />
      ),
    }),
    getSelectFilterDefinition({
      id: "topParentId",
      label: "Ultimate Parent",
      getSelectResult: (
        value,
        { filterVariantDefinition: { id: variantId } },
      ) => filterObj({ topParentId: { [variantId]: value } }),
      selectSchema: yup.string().uuid(),
      renderSelectInput: ({ field }, { isMulti }) => (
        <RetailerSelect
          {...field}
          isAdmin={isAdmin}
          isMulti={isMulti}
          menuPortalTarget={document.body}
          filter={{ isTopParent: { equalTo: true } }}
        />
      ),
    }),
    getBooleanFilterDefinition({ id: "isParent", label: "Is Parent?" }),
    getBooleanFilterDefinition({
      id: "isTopParent",
      label: "Is Ultimate Parent?",
    }),
    getBooleanFilterDefinition({ id: "hasParent", label: "Is Banner?" }),
    getBooleanFilterDefinition({
      id: "isCustom",
      label: "Custom?",
      getResult: (value, { filterVariantDefinition: { id: variantId } }) =>
        ["equalTo", "notEqualTo"].includes(variantId)
          ? { forBrandId: { isNull: variantId === "equalTo" ? !value : value } }
          : undefined,
    }),
    getIntegerFilterDefinition({
      id: "numStores",
      label: "Store Count",
      defaultVariant: "greaterThan",
    }),
    getBooleanFilterDefinition({
      id: "isRodeoPriority",
      label: "Rodeo Priority?",
    }),
    getBooleanFilterDefinition({
      id: "isAnchor",
      label: "Anchor Account?",
    }),
    getSelectFilterDefinition({
      id: "channels",
      label: "Channel",
      getSelectResult: makeGetManyResult("retailerRetailerChannels", (f) => ({
        retailerChannelId: f,
      })),
      selectSchema: yup.string().uuid(),
      renderSelectInput: ({ field }, { isMulti }) => (
        <RetailerChannelSelect
          {...field}
          isMulti={isMulti}
          menuPortalTarget={document.body}
        />
      ),
    }),
    getSelectFilterDefinition({
      id: "regions",
      label: "Region",
      getSelectResult: makeGetManyResult("retailerRetailerRegions", (f) => ({
        retailerRegionId: f,
      })),
      selectSchema: yup.string().uuid(),
      renderSelectInput: ({ field }, { isMulti }) => (
        <RetailerRegionSelect
          {...field}
          isMulti={isMulti}
          menuPortalTarget={document.body}
        />
      ),
    }),
    getSelectFilterDefinition({
      id: "slottingRequired",
      label: "Slotting Required?",
      getSelectResult: (
        value,
        { filterVariantDefinition: { id: variantId } },
      ) => ({ slottingRequired: { [variantId]: value } }),
      selectSchema: getEnumSchema<RetailerSlottingRequiredOption>(
        RetailerSlottingRequiredOption,
      ),
      renderSelectInput: ({ field }, { isMulti }) => (
        <EnumSelect
          {...field}
          isMulti={isMulti}
          enum={RetailerSlottingRequiredOption}
          labelsByValue={slottingOptionsMap}
          menuPortalTarget={document.body}
        />
      ),
    }),
    getSelectFilterDefinition({
      id: "pricingStrategy",
      label: "Pricing Strategy",
      getSelectResult: (
        value,
        { filterVariantDefinition: { id: variantId } },
      ) => ({ pricingStrategy: { [variantId]: value } }),
      selectSchema: getEnumSchema<RetailerPricingStrategyOption>(
        RetailerPricingStrategyOption,
      ),
      renderSelectInput: ({ field }, { isMulti }) => (
        <EnumSelect
          {...field}
          isMulti={isMulti}
          enum={RetailerPricingStrategyOption}
          labelsByValue={pricingStrategyMap}
          menuPortalTarget={document.body}
        />
      ),
    }),
    getSelectFilterDefinition({
      id: "categoryReview",
      label: "Category Review",
      getSelectResult: (
        value,
        { filterVariantDefinition: { id: variantId } },
      ) => ({ categoryReview: { [variantId]: value } }),
      selectSchema: getEnumSchema<RetailerCategoryReviewOption>(
        RetailerCategoryReviewOption,
      ),
      renderSelectInput: ({ field }, { isMulti }) => (
        <EnumSelect
          {...field}
          isMulti={isMulti}
          enum={RetailerCategoryReviewOption}
          labelsByValue={retailerCategoryReviewMap}
          menuPortalTarget={document.body}
        />
      ),
    }),
    ...addressFilterDefs,
    getStringSelectFilterDefinition({
      id: "distributor",
      label: "Distributor",
      getResult: makeGetManyResult("retailerOrderingPlatforms", (f) => ({
        orderingPlatform: { name: f },
      })),
      getSelectResult: makeGetManyResult("retailerOrderingPlatforms", (f) => ({
        orderingPlatformId: f,
      })),
      selectSchema: yup.string().uuid(),
      renderSelectInput: ({ field }, { isMulti }) => (
        <OrderingPlatformSelect
          {...field}
          isAdmin={isAdmin}
          isMulti={isMulti}
          menuPortalTarget={document.body}
        />
      ),
    }),
    getStringSelectFilterDefinition({
      id: "distributionCenter",
      label: "Distribution Center",
      getResult: makeGetDcResult((f) => ({ distributionCenter: { name: f } })),
      getSelectResult: makeGetDcResult((f) => ({ distributionCenterId: f })),
      selectSchema: yup.string().uuid(),
      renderSelectInput: ({ field }, { isMulti }) => (
        <DistributionCenterSelect
          {...field}
          isAdmin={isAdmin}
          isMulti={isMulti}
          menuPortalTarget={document.body}
          filter={{ name: { isNull: false } }}
        />
      ),
    }),
    getBooleanFilterDefinition({
      id: IS_ARCHIVED_FILTER_NAME,
      label: "Archived?",
      isHidden: true,
      getResult: (value, { filterVariantDefinition: { id: variantId } }) => {
        assertIsBoolean(value);
        return filterObj({ isArchived: { [variantId]: value } });
      },
    }),
    getDateTimeFilterDefinition({
      id: "createdAt",
      label: "Created at",
      defaultVariant: "greaterThan",
    }),
    getSelectFilterDefinition({
      id: "retailerType",
      label: "Retailer Type",
      isHidden: true,
      getSelectResult: (
        value,
        { filterVariantDefinition: { id: variantId } },
      ) => {
        const retailerTypes = getAssertedArrayOfType(
          value,
          makeAssertIsEnum<RetailerType>(RetailerType),
        );
        if (getIsEveryOptionSelected(retailerTypes)) return;
        return filterObj({ retailerType: { [variantId]: retailerTypes } });
      },
      selectSchema: getEnumSchema<RetailerType>(RetailerType),
      renderSelectInput: ({ field }, { isMulti }) => (
        <EnumSelect
          {...field}
          enum={RetailerType}
          labelsByValue={RETAILER_TYPE_LABELS}
          isMulti={isMulti}
          menuPortalTarget={document.body}
        />
      ),
    }),
  ];

  if (!isAdmin) return definitions;

  definitions.push(
    getIntegerFilterDefinition({
      id: "numActiveLocations",
      label: "Number of Active Locations",
      defaultVariant: "greaterThan",
      getResult: (value, { filterVariantDefinition: { id: variantId } }) =>
        filterObj({
          retailerLocations: {
            aggregates: {
              distinctCount: { id: { [variantId]: value } },
              filter: { isArchived: { equalTo: false } },
            },
          },
        }),
    }),
    getBooleanFilterDefinition({
      id: INCLUDE_CUSTOM_FILTER_NAME,
      label: "Include Custom?",
      isHidden: true,
      getResult: (value, { filterVariantDefinition: { id: variantId } }) => {
        if (
          (variantId === "equalTo" && value) ||
          (variantId === "notEqualTo" && !value)
        ) {
          return;
        }
        return filterObj({ isCustom: { equalTo: false } });
      },
    }),
  );

  return definitions;
}
